import * as React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";

import { formatNumber } from "@audacia-hq/shared/utils";
import SharedComponents from "@audacia-hq/shared/components";

import Button from "../Button";
import { useLocale } from "../../context/LocaleContext";
import { refreshPhoneNumberCode, validatePhoneNumber } from "../../services/api";
import { useWS, WSMessage } from "../../context/WSContext";
import { PhoneNumber } from "../../models/member";

interface Props {
  phoneNumber: string;
  memberCountry?: string;
  onClose: (error?: string) => void;
  onValidated: (phoneUid: string) => void;
}

interface PhoneValidationForm {
  phoneCode: string;
}

const PhoneNumberValidationModal: React.FC<Props> = ({
  phoneNumber, memberCountry, onClose, onValidated,
}) => {
  const { t } = useTranslation(["member", "common"]);
  const locale = useLocale();
  const ws = useWS();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [phone, setPhone] = useState<PhoneNumber>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedAttempts = parseInt(sessionStorage.getItem("pva") ?? "0", 10);
    sessionStorage.setItem("pva", `${savedAttempts + 1}`);
    setTimeout(() => setLoading(false), savedAttempts * 1200);
  }, []);

  const startPhoneNumberValidation = async (phoneNumber: string) => {
    const recaptcha = await executeRecaptcha("memberPhoneValidation");
    const { data, error } = await refreshPhoneNumberCode({
      variables: { phoneNumber, language: locale, recaptcha },
    });
    if (error) {
      onClose(error.messages[0]);
    } else if (data?.memberMutations?.refreshPhoneNumberCode) {
      setPhone(data.memberMutations.refreshPhoneNumberCode);
    }
  };

  const validateNumber = async (phoneNumber: string, phoneCode: string) => {
    const { data, error } = await validatePhoneNumber({
      variables: {
        input: { phoneNumber, phoneCode },
      },
    });
    if (data?.memberMutations?.validatePhoneNumber) {
      onValidated(data.memberMutations.validatePhoneNumber.phoneUid);
    } else {
      onClose(error?.messages[0]);
    }
  };

  useEffect(() => {
    if (loading) return;
    startPhoneNumberValidation(phoneNumber);
  }, [loading, phoneNumber]);

  useEffect(() => {
    const sub = ws.subscribe(async (msg: WSMessage) => {
      switch (msg.event) {
        case "member.phone.validation.success":
          onValidated(JSON.parse(msg.payload)?.phoneUid);
          break;
        case "member.phone.validation.failed":
          onClose(JSON.parse(msg.payload)?.failReason);
          break;
        default:
          break;
      }
    });
    return () => sub.unsubscribe();
  }, [ws]);

  const formik = useFormik<PhoneValidationForm>({
    initialValues: { phoneCode: "" },
    validationSchema: Yup.object().shape({
      phoneCode: Yup.string().required(t("common:errors.required")),
    }),
    onSubmit: async (form: PhoneValidationForm) => {
      validateNumber(phoneNumber, form.phoneCode);
    },
  });

  return (
    <SharedComponents.Modal title={t("phoneNumber.phoneValidationPopupTitle")} size="small" show onClose={onClose}>
      <div className="flex flex-col gap-5 p-4 items-center">
        {phone && !loading ? (
          <>
            <div>
              <Trans
                i18nKey={`phoneNumber.phoneValidationExplanation.${phone.type}`}
                ns="member"
                values={{
                  phoneNumber: formatNumber(phoneNumber, "INTERNATIONAL", memberCountry),
                }}
                components={[
                  <span className="font-semibold">number</span>,
                ]}
              />
            </div>

            {phone.type === "MOBILE" ? (
              <>
                <input
                  className="input px-4 text-center text-xl tracking-widest max-w-sm"
                  placeholder="1234"
                  id="phoneCode"
                  value={formik.values.phoneCode}
                  onChange={formik.handleChange}
                />
                {formik.errors.phoneCode && <span className="text-sm text-red-500">{formik.errors.phoneCode}</span>}

                <Button type="submit" onClick={() => formik.submitForm()}>
                  {t("common:validate")}
                </Button>
              </>
            ) : <span className="font-semibold text-center text-4xl tracking-widest mb-8">{phone.phoneCode}</span>}
          </>
        ) : <FontAwesomeIcon icon={faCircleNotch} className="animate-spin" />}
      </div>
    </SharedComponents.Modal>
  );
};

export default PhoneNumberValidationModal;
